class BlogFilters {
  constructor() {
    this.articles = document.querySelectorAll('.blog-articles__article:not(.blog-articles__article--featured)');
    this.featuredArticle = document.querySelector('.blog-articles__article--featured');
    this.featuredContainer = document.querySelector('.blog-featured-article');
    this.categoryButtons = document.querySelectorAll('.blog-filter-category');
    this.tagButtons = document.querySelectorAll('.blog-filter-tag');
    this.clearButton = document.querySelector('.blog-filter-clear');
    this.articleCount = document.querySelector('.blog-article-count');
    this.activeFilters = {
      category: null,
      tags: []
    };
    
    this.init();
  }

  init() {
    // Sort and display top 10 most common tags
    this.sortTagsByCount();
    
    // Read filters from URL on page load
    this.readFiltersFromURL();
    
    // Apply initial filters
    this.applyFilters();
    
    // Set up event listeners
    this.setupEventListeners();
    
    // Handle browser back/forward navigation
    window.addEventListener('popstate', () => {
      this.readFiltersFromURL();
      this.applyFilters();
    });
  }

  sortTagsByCount() {
    const tagButtonsContainer = document.querySelector('.blog-filter-group-tags .blog-filter-buttons');
    if (!tagButtonsContainer) return;
    
    const tagCountsData = tagButtonsContainer.dataset.tagCounts;
    
    // Get all tag buttons
    const allTagButtons = Array.from(tagButtonsContainer.querySelectorAll('.blog-filter-tag'));
    
    // Deduplicate tags - keep only the first occurrence of each tag
    const seenTags = new Set();
    const uniqueTagButtons = [];
    const duplicatesToRemove = [];
    
    allTagButtons.forEach((button, index) => {
      const tag = button.dataset.tag;
      const normalizedTag = tag.toLowerCase().trim();
      
      if (seenTags.has(normalizedTag)) {
        // This is a duplicate, mark for removal
        duplicatesToRemove.push(button);
      } else {
        seenTags.add(normalizedTag);
        uniqueTagButtons.push(button);
      }
    });
    
    // Remove duplicate buttons from DOM
    duplicatesToRemove.forEach(button => button.remove());
    
    if (!tagCountsData) {
      // If no count data, just show first 10 unique tags
      uniqueTagButtons.forEach((tag, index) => {
        if (index < 10) {
          tag.style.display = '';
        } else {
          tag.style.display = 'none';
        }
      });
      return;
    }
    
    // Parse tag counts: "tag1:count1|tag2:count2|..."
    const tagCounts = {};
    const countPairs = tagCountsData.split('|');
    countPairs.forEach(pair => {
      const [tag, count] = pair.split(':');
      if (tag && count) {
        const normalizedTag = tag.toLowerCase().trim();
        // Use the normalized tag as key, but keep original for display
        tagCounts[normalizedTag] = parseInt(count, 10) || 0;
        // Also map original tag if different
        if (tag.toLowerCase().trim() !== tag) {
          tagCounts[tag] = parseInt(count, 10) || 0;
        }
      }
    });
    
    // Sort by count (descending), then alphabetically
    uniqueTagButtons.sort((a, b) => {
      const tagA = a.dataset.tag;
      const tagB = b.dataset.tag;
      const normalizedA = tagA.toLowerCase().trim();
      const normalizedB = tagB.toLowerCase().trim();
      
      // Try to get count with normalized tag first, then original
      const countA = tagCounts[normalizedA] || tagCounts[tagA] || 0;
      const countB = tagCounts[normalizedB] || tagCounts[tagB] || 0;
      
      if (countA !== countB) {
        return countB - countA; // Higher count first
      }
      return tagA.localeCompare(tagB); // Alphabetical if same count
    });
    
    // Reorder in DOM and show top 10
    uniqueTagButtons.forEach((button, index) => {
      tagButtonsContainer.appendChild(button);
      if (index < 10) {
        button.style.display = '';
      } else {
        button.style.display = 'none';
      }
    });
  }

  readFiltersFromURL() {
    const urlParams = new URLSearchParams(window.location.search);
    const category = urlParams.get('category');
    const tags = urlParams.get('tags');
    
    this.activeFilters.category = category || null;
    this.activeFilters.tags = tags ? tags.split(',').filter(t => t) : [];
    
    // Update button states
    this.updateButtonStates();
  }

  updateButtonStates() {
    // Update category buttons
    this.categoryButtons.forEach(btn => {
      const category = btn.dataset.category;
      if (category === this.activeFilters.category) {
        btn.classList.add('active');
      } else {
        btn.classList.remove('active');
      }
    });
    
    // Update tag buttons
    this.tagButtons.forEach(btn => {
      const tag = btn.dataset.tag;
      if (this.activeFilters.tags.includes(tag)) {
        btn.classList.add('active');
      } else {
        btn.classList.remove('active');
      }
    });
  }

  setupEventListeners() {
    // Re-query tag buttons after sorting (they may have been reordered)
    this.tagButtons = document.querySelectorAll('.blog-filter-tag');
    
    // Category filter buttons
    this.categoryButtons.forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        const category = btn.dataset.category;
        
        if (this.activeFilters.category === category) {
          // Deselect if already active
          this.activeFilters.category = null;
        } else {
          this.activeFilters.category = category;
        }
        
        this.applyFilters();
        this.updateURL();
      });
    });
    
    // Tag filter buttons
    this.tagButtons.forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        const tag = btn.dataset.tag;
        const index = this.activeFilters.tags.indexOf(tag);
        
        if (index > -1) {
          // Remove tag if already active
          this.activeFilters.tags.splice(index, 1);
        } else {
          // Add tag
          this.activeFilters.tags.push(tag);
        }
        
        this.applyFilters();
        this.updateURL();
      });
    });
    
    // Clear filters button
    if (this.clearButton) {
      this.clearButton.addEventListener('click', (e) => {
        e.preventDefault();
        this.activeFilters.category = null;
        this.activeFilters.tags = [];
        this.applyFilters();
        this.updateURL();
      });
    }
  }

  applyFilters() {
    let visibleCount = 0;
    
    // Determine if any filters are active
    const hasCategoryFilter = this.activeFilters.category !== null && this.activeFilters.category !== '';
    const hasTagFilter = this.activeFilters.tags.length > 0;
    const hasAnyFilter = hasCategoryFilter || hasTagFilter;
    
    // Filter regular articles
    this.articles.forEach(article => {
      const articleCategory = article.dataset.category || '';
      const articleTags = article.dataset.tags ? article.dataset.tags.split(',').map(t => t.trim()) : [];
      
      let matchesCategory = true;
      let matchesTags = true;
      
      // If no filters are active, show all articles
      if (!hasAnyFilter) {
        article.style.display = '';
        article.classList.remove('blog-article-hidden');
        visibleCount++;
        return;
      }
      
      // Check category filter
      if (hasCategoryFilter) {
        matchesCategory = articleCategory === this.activeFilters.category;
      }
      
      // Check tag filters (article must have at least one of the selected tags)
      if (hasTagFilter) {
        matchesTags = this.activeFilters.tags.some(tag => articleTags.includes(tag));
      }
      
      // Show/hide article based on filters
      if (matchesCategory && matchesTags) {
        article.style.display = '';
        article.classList.remove('blog-article-hidden');
        visibleCount++;
      } else {
        article.style.display = 'none';
        article.classList.add('blog-article-hidden');
      }
    });
    
    // Filter featured article if it exists
    if (this.featuredArticle) {
      // If no filters are active, show featured article
      if (!hasAnyFilter) {
        this.featuredArticle.style.display = '';
        this.featuredArticle.classList.remove('blog-article-hidden');
        if (this.featuredContainer) {
          this.featuredContainer.style.display = '';
        }
        visibleCount++;
      } else {
        const featuredCategory = this.featuredArticle.dataset.category || '';
        const featuredTags = this.featuredArticle.dataset.tags ? this.featuredArticle.dataset.tags.split(',').map(t => t.trim()) : [];
        
        let matchesCategory = true;
        let matchesTags = true;
        
        if (hasCategoryFilter) {
          matchesCategory = featuredCategory === this.activeFilters.category;
        }
        
        if (hasTagFilter) {
          matchesTags = this.activeFilters.tags.some(tag => featuredTags.includes(tag));
        }
        
        if (matchesCategory && matchesTags) {
          this.featuredArticle.style.display = '';
          this.featuredArticle.classList.remove('blog-article-hidden');
          if (this.featuredContainer) {
            this.featuredContainer.style.display = '';
          }
          visibleCount++;
        } else {
          this.featuredArticle.style.display = 'none';
          this.featuredArticle.classList.add('blog-article-hidden');
          if (this.featuredContainer) {
            this.featuredContainer.style.display = 'none';
          }
        }
      }
    }
    
    // Update article count
    if (this.articleCount) {
      this.articleCount.textContent = `${visibleCount} ${visibleCount === 1 ? 'article' : 'articles'}`;
    }
    
    // Update button states
    this.updateButtonStates();
    
    // Show/hide clear button
    if (this.clearButton) {
      const hasActiveFilters = this.activeFilters.category || this.activeFilters.tags.length > 0;
      this.clearButton.style.display = hasActiveFilters ? '' : 'none';
    }
  }

  updateURL() {
    const url = new URL(window.location);
    
    // Remove existing filter params
    url.searchParams.delete('category');
    url.searchParams.delete('tags');
    
    // Add active filters
    if (this.activeFilters.category) {
      url.searchParams.set('category', this.activeFilters.category);
    }
    
    if (this.activeFilters.tags.length > 0) {
      url.searchParams.set('tags', this.activeFilters.tags.join(','));
    }
    
    // Update URL without reload
    window.history.pushState({}, '', url);
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    if (document.querySelector('.blog-filters')) {
      new BlogFilters();
    }
  });
} else {
  if (document.querySelector('.blog-filters')) {
    new BlogFilters();
  }
}

